<template>
	<div class="date-time-widget">
		<div class="date-time-widget-datcol" ref="datColumn">
			<div class="month-drop-row">
				<drop-label
					:label="dropMonthLabel"
					:open="monthPickMode"
					@click.prevent.stop="togglePickMode"
					style="font-size: var(--font-size-s)"></drop-label>
				<mini-pager v-if="!monthPickMode" :disable-left="disabled" :disable-right="disabled" @left="prevMonth" @right="nextMonth"></mini-pager>
			</div>
			<div v-if="monthPickMode" class="month-pick">
				<roller-select :list="monthList" v-model="calMonth" style="text-align: right"></roller-select>
				<roller-select :list="yearList" v-model="calYear"></roller-select>
			</div>
			<div v-else class="calendar-pick">
				<calendar-widget v-model="dateValue" :month="calMonth" :year="calYear"></calendar-widget>
			</div>
		</div>
		<div class="date-time-widget-timcol" v-if="supportTime">
			<div class="time-enable-row">
				<label class="time-label fd-label">{{ env.msg("time") }}</label>
				<a-switch v-model="hasTime"></a-switch>
			</div>
			<time-widget v-model="timeValue"></time-widget>
		</div>
	</div>
</template>

<script setup lang="ts">
import CalendarWidget from "./CalendarWidget.vue";
import TimeWidget from "./TimeWidget.vue";
import DropLabel from "../../helpers/DropLabel.vue";
import RollerSelect from "../../helpers/RollerSelect.vue";
import MiniPager from "../../helpers/MiniPager.vue";
import { formatDate, parseDate } from "@t/util/DateUtil";
import ASwitch from "../../components/ASwitch.vue";
import {computed, ref} from "vue";
import {injectEnv} from "../../Environment";

const props = defineProps({
  modelValue: String,
  policy: {
    type: String,
    default: "datetime", // Other values are "universal", "date"
  },
  disabled: Boolean,
});
const emit = defineEmits(['update:modelValue']);
const env = injectEnv();

let initDate = parseDate(props.modelValue);
if (initDate.date == null) initDate.date = new Date();
let m = initDate.date.getMonth().toString();
let y = initDate.date.getFullYear().toString();

const calMonth = ref<number>(initDate.date.getMonth());
const calYear = ref<number>(initDate.date.getFullYear());
const monthPickMode = ref(false);
const datColumn = ref<HTMLDivElement|null>(null);

const MONTH_FORMAT = new Intl.DateTimeFormat(undefined, {
	month: "long",
});

const hasTime =computed({
  get() {
    let d = parseDate(props.modelValue);
    return d.date != null && !d.wholeDay;
  },
  set(v) {
    if (v) {
      console.log("Adding time to " + props.modelValue);
      // add time
      if (supportTime.value) {
        let d = parseDate(props.modelValue);
        if (d.date == null) {
          d.date = new Date();
          d.wholeDay = true;
        }
        if (d.wholeDay) {
          d.date.setHours(10);
          d.date.setMinutes(0);
          d.date.setSeconds(0);
          d.wholeDay = false;
          emit("update:modelValue", formatDate(d.date, true, false));
        }
      }
    } else {
      console.log("Removing time from " + props.modelValue);
      // remove time
      if (props.policy !== "datetime") {
        let d = parseDate(props.modelValue);
        if (d.date != null && !d.wholeDay) {
          emit("update:modelValue", formatDate(d.date, false));
        }
      }
    }
  },
});

const dateValue = computed({
  get() {
    return props.modelValue;
  },
  set(v) {
    emit("update:modelValue", v);
  },
});

const timeValue = computed({
  get() {
    return props.modelValue;
  },
  set(v) {
    emit("update:modelValue", v);
  },
});

const supportTime = computed(() => {
  return props.policy !== "date";
});
const optionalTime = computed(() => {
  return props.policy === "universal";
});
const dropMonthLabel = computed(() => {
  const d = new Date();
  d.setMonth(calMonth.value);
  return MONTH_FORMAT.format(d).toLowerCase() + " " + calYear.value;
});
const monthList = computed(() => {
  let l = [];
  const d = new Date();
  d.setDate(1);
  for (let m = 0; m < 12; m++) {
    d.setMonth(m);
    l.push({ value: m.toString(), name: MONTH_FORMAT.format(d) });
  }
  return l;
});
const yearList = computed(() => {
  let l = [];
  const d = new Date();
  const max = d.getFullYear() + 10;
  for (let y = d.getFullYear() - 10; y <= max; y++) {
    let yer = y.toString();
    l.push({ value: yer, name: yer });
  }
  return l;
});


function togglePickMode() {
  if (!monthPickMode.value && datColumn.value) {
    datColumn.value.style.width = datColumn.value.offsetWidth + "px";
  }
  monthPickMode.value = !monthPickMode.value;
}

function prevMonth() {
  let m = calMonth.value;
  let y = calYear.value;
  m--;
  if (m < 0) {
    m = 11; // december
    y--;
  }
  calMonth.value = m;
  calYear.value = y;
}

function nextMonth() {
  let m = calMonth.value;
  let y = calYear.value;
  m++;
  if (m >= 12) {
    m = 0; // january
    y++;
  }
  calMonth.value = m;
  calYear.value = y;
}
</script>

<style>
.date-time-widget {
	display: flex;
	flex-direction: row;
	column-gap: var(--gap);
	align-items: flex-start;
	justify-content: space-between;
}
.date-time-widget-datcol,
.date-time-widget-timcol {
	display: flex;
	flex-direction: column;
}
.date-time-widget-datcol {
	flex: 1 1;
}
.date-time-widget-timcol {
	margin-right: var(--gap);
	border-left: var(--hair) solid var(--border-color);
	padding-left: var(--gap);
	flex: 0 0;
}
.month-drop-row {
	display: flex;
	flex-direction: row;
	align-items: baseline;
	justify-content: space-between;
	font-size: var(--font-size-s);
	height: 32px;
	line-height: 32px;
}
.month-pick {
	display: flex;
	flex-direction: row;
	column-gap: 8px;
	align-items: flex-start;
	justify-content: center;
}
.time-enable-row {
	display: flex;
	flex-direction: row;
	column-gap: 4px;
	align-items: center;
	justify-content: space-between;
	height: 32px;
	line-height: 30px;
	padding-top: 2px;
}
.time-label {
}
</style>
